/**
 * Sistema de Logging Seguro para Produção
 * Remove logs sensíveis em ambiente de produção
 */

const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development'
const isProduction = import.meta.env.PROD || import.meta.env.MODE === 'production'

/**
 * Logger condicional - só loga em desenvolvimento
 */
export const logger = {
  /**
   * Log de desenvolvimento - NÃO aparece em produção
   */
  dev: (...args: unknown[]) => {
    if (isDevelopment) {
      console.log('[DEV]', ...args)
    }
  },

  /**
   * Log de informação - aparece em produção mas ofuscado
   */
  info: (...args: unknown[]) => {
    if (isDevelopment) {
      console.info('[INFO]', ...args)
    } else {
      // Em produção, apenas indica que algo aconteceu sem detalhes
      console.info('[INFO]', 'Operação executada')
    }
  },

  /**
   * Warning - sempre aparece mas sem dados sensíveis em produção
   */
  warn: (message: string, data?: unknown) => {
    if (isDevelopment) {
      console.warn('[WARN]', message, data)
    } else {
      console.warn('[WARN]', message)
    }
  },

  /**
   * Erro - sempre aparece (necessário para debugging)
   */
  error: (message: string, error?: unknown) => {
    console.error('[ERROR]', message, error)
  },

  /**
   * Log de dados sensíveis - NUNCA aparece em produção
   */
  sensitive: (message: string, data?: unknown) => {
    if (isDevelopment) {
      console.log('[SENSITIVE]', message, data)
    }
    // Em produção: silêncio total
  },

  /**
   * Performance - só em desenvolvimento
   */
  perf: (label: string, timeMs: number) => {
    if (isDevelopment) {
      console.log(`[PERF] ${label}: ${timeMs}ms`)
    }
  }
}

/**
 * Ofuscar URL para logs seguros
 * Remove informações sensíveis mantendo contexto
 */
export const obfuscateUrl = (url: string): string => {
  if (!url) return 'empty'
  
  try {
    const urlObj = new URL(url)
    const pathname = urlObj.pathname
    
    // Pegar apenas o último segmento (nome do arquivo)
    const segments = pathname.split('/').filter(Boolean)
    const filename = segments[segments.length - 1] || 'unknown'
    
    // Ofuscar número da carta e valor de Ephis
    const obfuscated = filename
      .replace(/\d+/g, '***')  // Substituir números por ***
      .replace(/EPHI\d+/gi, 'EPHI***')  // Ofuscar valores de Ephis
    
    return `***/${obfuscated}`
  } catch {
    return '***'
  }
}

/**
 * Ofuscar informações de carta
 */
export const obfuscateCardInfo = (card: {
  name?: string
  series_name?: string
  image_url?: string
  [key: string]: unknown
}): Record<string, unknown> => {
  if (isDevelopment) {
    return card
  }
  
  // Em produção, retornar apenas dados não-sensíveis
  return {
    has_image: !!card.image_url,
    has_series: !!card.series_name
  }
}

/**
 * Helper para timing de performance
 */
export const createTimer = (label: string) => {
  const start = performance.now()
  
  return {
    end: () => {
      const duration = performance.now() - start
      logger.perf(label, Math.round(duration))
      return duration
    }
  }
}

/**
 * Verificar se está em produção
 */
export const isProductionMode = () => isProduction

/**
 * Verificar se está em desenvolvimento
 */
export const isDevelopmentMode = () => isDevelopment

